/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import styled from "styled-components";
import { Box, Table, theme } from "@nordcloud/gnui";
import {
  EventDetailResourceFragment,
  EventDetailResourceGroupFragment,
  EventStatus,
  Group,
  IncomingEventStatus,
  Resource,
} from "~/generated/graphql";
import { DuplicateResourceState } from "~/hooks/useDuplicatedResources";
import { isEmpty } from "~/tools";
import { DuplicateResourceIcon } from "./DuplicateResourceIcon";
import { ResourceGroupItem } from "./ResourceGroupItem";
import { ResourceItem } from "./ResourceItem";
import { ResourcesNoData } from "./ResourcesNoData";

export type ResourceGroup = EventDetailResourceGroupFragment & {
  resources?: Resource[];
};

type Props = {
  resourceGroups: ResourceGroup[] | Group[];
  resources: EventDetailResourceFragment[] | Resource[];
  status?: EventStatus | IncomingEventStatus;
  duplicateState?: DuplicateResourceState;
};

export function ResourceList({
  resourceGroups,
  resources,
  status,
  duplicateState,
}: Props) {
  if (isEmpty(resourceGroups) && isEmpty(resources)) {
    return <ResourcesNoData />;
  }

  return (
    <>
      <WrapResourceGroups>
        {resourceGroups.map((resourceGroup) => {
          return (
            <ResourceGroupItem
              key={resourceGroup.id}
              resourceGroup={resourceGroup}
              status={status}
              duplicateState={duplicateState}
            />
          );
        })}
      </WrapResourceGroups>
      <WrapResources>
        {resources.map((resource) => {
          return (
            <Box
              key={resource.id}
              innerSpacing="spacing00"
              css={{ marginBottom: theme.spacing.spacing02 }}
            >
              <Table>
                <Table.tbody>
                  <ResourceItem
                    resource={resource}
                    status={status}
                    highlight={duplicateState?.isHighlighted(resource.id)}
                    actions={
                      duplicateState?.duplicateResourceIds?.has(resource.id)
                        ? () => (
                            <DuplicateResourceIcon
                              resourceId={resource.id}
                              onIconClick={duplicateState?.onDuplicateIconClick}
                            />
                          )
                        : undefined
                    }
                  />
                </Table.tbody>
              </Table>
            </Box>
          );
        })}
      </WrapResources>
    </>
  );
}

const WrapResourceGroups = styled.div`
  & > :not(:first-child) {
    margin-top: ${theme.spacing.spacing04};
  }
`;

const WrapResources = styled.div`
  margin-top: ${theme.spacing.spacing04};
`;
